import { observable, action, computed } from 'mobx';
import { UserEvents, UserEventStatus } from './constants';
import { UseLiveChatSDKApp, isBank, isSupportChatbot } from '@/config/core';
import { getABTestConfig, getNewABTestConfig } from '@/utils/bridges/getABTestConfig';
import firstInitStore from '../firstInit';
import API from '@/services';
import { initLiveChatSDK, isBankNonLogin } from '@/utils/liveChatToken';
import reporter from '@/utils/report';
import { setSearchUrl } from '@/config/search';

let resolve;

export class UserStore {
  @observable userId = '';

  @observable chatbot_user_id = '';

  @observable userName = '';

  @observable phone = '';

  @observable email = '';

  @observable avatar = '';

  @observable loyaltyTierCode = 0;

  @observable loyaltyTierName = '';

  @observable isSeller = false;

  @observable isLogin = false;

  // 频控用 - login时间戳
  @observable showLoginTimestamp = null;

  @observable maxSize = 5 * 1024 * 1024;

  @observable loading = true;

  // 缓存函数列表，在调用user_info接口成功后在调用
  @observable cacheFuncList = [];

  // 所有用户相关事件
  @observable userEvents = { [UserEvents.GetHistoryPrompt]: UserEventStatus.Default };

  // 灰度控制
  @observable abTestingConfigs = {};

  // 新实验平台 abTest
  @observable newABTestingConfigs = {};
  @observable hasFetchedNewABTestingConfigs = false;

  // 灰度控制
  @observable abTestingSignature = '';

  // 是否点击过 virtual call
  @observable isClickVirtualCall = false;

  @observable userInfoPromise = new Promise((rs) => {
    resolve = rs;
  });

  // live agent case 数据存储
  @observable liveAgentCaseData = {
    active: false,
    caseList: [],
    isButtonsDisabled: false,
    page_no: 0,
    enquiryCaseMaxSize: 0,
  };

  constructor() {
    this.userId = '';
    this.userName = '';
    this.email = '';
    this.phone = '';
    this.avatar = '';
    this.loyaltyTierCode = 0;
    this.loyaltyTierName = '';
    this.isSeller = false;
    this.showLoginTimestamp = null;
    this.abTestingConfigs = {};
    this.abTestingSignature = '';
    this.chatbot_user_id = '';
    setTimeout(() => {
      setSearchUrl()
        .catch(() => {
          // do nothing
        })
        .finally(() => {
          if (isSupportChatbot()) {
            this.FETCH_DATA();
          } else {
            this.INIT_FOR_LIVE_CHAT();
          }
        });
    }, 0);
  }

  @computed
  get ifShowLoginOverfast() {
    if (this.showLoginTimestamp) {
      const now = new Date().getTime();
      return now - this.showLoginTimestamp < 2000;
    } else {
      return false;
    }
  }

  async fetchUserEvents() {
    await firstInitStore.initApiPromise;

    const eventIds = [UserEvents.GetHistoryPrompt];

    // TODO:plz remove
    let result;
    if (isBank() && isBankNonLogin) {
      result = null;
    } else {
      result = await API.getUserEvents({ event_ids: eventIds }).catch(() => { });
    }
    if (result && Array.isArray(result)) {
      this.UPDATE_USER_EVENTS(this.formatUserEvents(result));
    }
  }

  @action
  INIT_FOR_LIVE_CHAT() {
    resolve();
    this.UPDATE_IS_LOGIN(true);
    this.UPDATE_DATA({});
    this.fetchUserEvents();
  }

  @action
  async FETCH_DATA(reLogin = false) {
    this.UPDATE_LOADING(true);

    if (UseLiveChatSDKApp.is()) {
      await initLiveChatSDK();
    }

    const preGetUserInfo = await window.preRequest?.preGetUserInfo;
    const userInfo = preGetUserInfo || (await API?.getUserInfo?.().catch(() => { })) || {};

    reporter.apiLog(`【getUserInfo】result: ${JSON.stringify(userInfo)}`, { level: 'debug' });

    const isLogin = JSON.stringify(userInfo) !== '{}';

    resolve();

    this.UPDATE_IS_LOGIN(isLogin);
    this.UPDATE_DATA({ ...userInfo });
    this.UPDATE_LOADING(false);
    this.execFuncList();

    if (isLogin) {
      setTimeout(() => {
        this.fetchUserEvents(); // 延后请求，减少首屏请求
      }, 3000);
    }

    if (reLogin) {
      this.FETCH_ABTEST();
    }
  }

  async FETCH_ABTEST() {
    try {
      await getABTestConfig();
      await getNewABTestConfig();
    } catch (error) {
      console.error('fetch newABTestingConfigs error');
    }
    this.UPDATE_HAS_FETCHED_NEW_ABTEST_CONFIG(true);
  }

  @action
  execFuncList() {
    if (this.cacheFuncList.length > 0) {
      this.cacheFuncList.map((fn) => fn());

      this.UPDATE_CACHE_FUNC_LIST([]);
    }
  }

  @action
  execWhenLoaded(cb) {
    if (!this.loading) {
      if (cb) {
        cb();
      }
      return;
    }

    this.cacheFuncList.push(cb);
  }

  @action
  UPDATE_IS_LOGIN(isLogin) {
    this.isLogin = isLogin;
  }

  @action
  async UPDATE_DATA({
    user_id = '',
    user_name = '',
    phone = '',
    email = '',
    avatar = '',
    loyalty_tier_code = 0,
    loyalty_tier_name = '',
    is_seller = false,
    chatbot_user_id = '',
  }) {
    this.userId = user_id;
    this.userName = user_name;
    this.phone = phone;
    this.email = email;
    this.avatar = avatar;
    this.loyaltyTierCode = loyalty_tier_code;
    this.loyaltyTierName = loyalty_tier_name;
    this.account_type = is_seller ? 'Seller' : 'Buyer';
    this.chatbot_user_id = chatbot_user_id;

    const { default: initStore } = await import('@/stores/init');
    reporter.apiLog(`【UPDATE_DATA】userId: ${user_id}; sessionId=${initStore.sessionId}`, { level: 'debug' });
  }

  @action
  UPDATE_IS_SELLER(flag = false) {
    this.isSeller = flag;
  }

  @action
  UPDATE_SHOWLOGIN_TIMESTAMP() {
    this.showLoginTimestamp = new Date().getTime();
  }

  @action
  UPDATE_LOADING(loading) {
    this.loading = loading;
  }

  @action
  UPDATE_CACHE_FUNC_LIST(cacheFuncList) {
    this.cacheFuncList = cacheFuncList;
  }

  formatUserEvents(userEvents) {
    if (Array.isArray(userEvents)) {
      return userEvents.reduce(
        (acc, cur) => {
          acc[cur.event_id] = cur.status;
          return acc;
        },
        { ...this.userEvents },
      );
    }

    return {};
  }

  /**
   * 更新用户事件状态
   * @param {event_id: UserEvents; status: UserEventStatus} userEvents
   */
  @action
  UPDATE_USER_EVENTS(userEvents) {
    const newEvents = {
      ...this.userEvents,
      ...userEvents,
    };

    this.userEvents = newEvents;
  }

  @action
  UPDATE_ABTEST_CONFIG(configs = {}) {
    this.abTestingConfigs = configs;
  }

  @action
  UPDATE_NEW_ABTEST_CONFIG(configs = {}) {
    this.newABTestingConfigs = configs;
  }

  @action
  UPDATE_HAS_FETCHED_NEW_ABTEST_CONFIG(bool = false) {
    this.hasFetchedNewABTestingConfigs = bool;
  }

  @action
  UPDATE_ABTEST_SIGNATURE(signature = '') {
    this.abTestingSignature = signature;
  }

  @action
  UPDATE_IS_CLICK_VIRTUAL_CALL(bool = false) {
    this.isClickVirtualCall = bool;
  }

  @action
  UPDATE_LIVE_AGENT_CASE_DATA(data = {}) {
    this.liveAgentCaseData = {
      ...this.liveAgentCaseData,
      ...data,
    };
  }

  async HANDLE_CASE_CLICK_FETCH() {
    const params = {
      page_no: 1,
      page_size: this.liveAgentCaseData.enquiryCaseMaxSize,
    };
    const { case_items } = await (API).queryUserCaseList(params).catch(() => { });
    this.UPDATE_LIVE_AGENT_CASE_DATA({
      active: true,
      caseList: case_items || [],
      page_no: 2,
    });
  }
}

const userStore = new UserStore();

export default userStore;
